import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Download, Upload, Send, Bell, Link2, Trash2, ExternalLink, Plus, X } from "lucide-react";
import { api, signedFileUrl } from "@/lib/api";
import { Section, Chip, StatusBadge } from "@/components/ui-brand";
import { ProcesoFinalizado } from "@/components/egresado/ProcesoFinalizado";
import { PanelHeader, Tabs, TagInput, Field, Card } from "@/components/panel";
import { useAuth } from "@/context/AuthContext";
import { FortalezasSelector } from "@/components/egresado/FortalezasSelector";

export default function EgresadoPanel() {
  const initTab = new URLSearchParams(window.location.search).get("tab") || "perfil";
  const [tab, setTab] = useState(initTab);
  const [profile, setProfile] = useState(null);
  const { logout } = useAuth();
  const [del, setDel] = useState({ open: false, password: "", confirm: "" });
  const deleteAccount = async () => {
    try { await api.delete("/egresado/account", { data: { password: del.password, confirmacion: del.confirm } }); toast.success("Tu cuenta y tus datos fueron eliminados"); logout(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar la cuenta"); }
  };
  const [informe, setInforme] = useState({ areas: [], respaldadas: [], publicas: [], seleccion_inicial: [], pendiente_revision: false });
  const [catalogs, setCatalogs] = useState({ skill_hard: [], skill_soft: [], formacion: [] });
  const [busquedas, setBusquedas] = useState([]);
  const [posts, setPosts] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [emp, setEmp] = useState(null);
  const [bio, setBio] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", ubicacion: "", skill: "" });

  const loadProfile = () => api.get("/egresado/profile").then((r) => { setProfile(r.data); setBio(r.data.cv_generado?.texto || r.data.bio_publica || ""); });
  const loadInforme = () => api.get("/egresado/informe").then((r) => setInforme(r.data));
  const loadBusquedas = () => api.get(`/busquedas?q=${filters.q}&ubicacion=${filters.ubicacion}&skill=${filters.skill}`).then((r) => setBusquedas(r.data.items));

  useEffect(() => {
    loadProfile(); loadInforme();
    api.get("/catalogs").then((r) => setCatalogs(r.data));
    api.get("/egresado/emprendimiento").then((r) => setEmp(r.data));
    loadBusquedas();
    api.get("/egresado/postulaciones").then((r) => setPosts(r.data.items));
    api.get("/egresado/notifications").then((r) => setNotifs(r.data.items));
    // eslint-disable-next-line
  }, []);

  const saveProfile = async (patch) => {
    const r = await api.put("/egresado/profile", patch);
    setProfile(r.data);
    const auto = r.data.skills_autocompletadas || [];
    toast.success(auto.length ? `Perfil guardado. Sumamos ${auto.length} habilidad(es) técnica(s) de tu formación; podés editarlas en "Mis habilidades".` : "Perfil guardado");
  };

  const toggleVisibleEmpresas = async () => {
    const next = !(profile.visible_para_empresas?.activo);
    const r = await api.put("/egresado/visibilidad", { activo: next });
    setProfile({ ...profile, visible_para_empresas: r.data });
    toast.success(next ? "Ahora sos visible para las empresas" : "Ya no aparecés en las búsquedas de empresas");
  };

  const uploadCert = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("institucion", profile.marca); fd.append("fecha", "2025");
    await api.post("/egresado/certificados", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Certificado subido"); loadProfile();
  };

  const uploadCvPropio = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/egresado/cv-propio", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("CV propio subido"); loadProfile(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
  };

  const uploadPortfolioImg = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("titulo", f.name.replace(/\.[^.]+$/, ""));
    try { await api.post("/egresado/portfolio/imagen", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Imagen subida al portfolio"); loadProfile(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al subir la imagen"); }
    finally { e.target.value = ""; }
  };

  const uploadFoto = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/egresado/foto", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Foto de perfil actualizada"); loadProfile(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al subir la foto"); }
    finally { e.target.value = ""; }
  };

  const generateBio = async () => {
    setGenLoading(true);
    try { const r = await api.post("/egresado/ai/generate-bio"); setBio(r.data.texto); toast.success("Texto generado con IA"); }
    catch { toast.error("No se pudo generar"); } finally { setGenLoading(false); }
  };
  const saveCv = async () => { await api.post("/egresado/cv/save", { texto: bio }); toast.success("Perfil guardado"); loadProfile(); };
  const downloadPdf = async () => {
    const r = await api.get("/egresado/cv/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "CV.pdf"; a.click(); URL.revokeObjectURL(url);
    toast.success("CV exportado a PDF");
  };
  const postular = async (id) => { try { await api.post(`/egresado/${id}/postular`); toast.success("Postulación enviada"); loadBusquedas(); api.get("/egresado/postulaciones").then((r) => setPosts(r.data.items)); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const saveEmp = async () => { await api.post("/egresado/emprendimiento", emp); toast.success("Emprendimiento guardado"); };

  const unread = notifs.filter((n) => !n.leida).length;
  const tabs = [
    { key: "perfil", label: "1. Mi Perfil" },
    { key: "habilidades", label: "2. Mis habilidades" },
    { key: "experiencia", label: "3. Mi experiencia" },
    { key: "ia", label: "4. Asistente IA / CV" },
    { key: "empleos", label: "Empleos" },
    { key: "postulaciones", label: "Postulaciones" },
    { key: "emprendimiento", label: "Tu emprendimiento" },
    { key: "notif", label: <span className="inline-flex items-center gap-1.5"><Bell size={14} /> Notificaciones</span>, badge: unread || null },
  ];

  if (!profile) return <div className="py-20 text-center font-heading text-[var(--brand-primary)]">Cargando…</div>;

  return (
    <div>
      <PanelHeader title={`Hola, ${profile.name?.split(" ")[0] || "egresado"}`} subtitle={`${profile.marca} · ${profile.ubicacion || "Córdoba"}`}
        right={<button onClick={downloadPdf} className="btn-accent flex items-center gap-2" data-testid="header-cv-download"><Download size={16} /> Descargar CV</button>} />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <Section className="py-8">
        {tab === "perfil" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-4">Datos básicos</h3>
              <div className="space-y-3">
                <Field label="Nombre" defaultValue={profile.name} onBlur={(e) => saveProfile({ name: e.target.value })} data-testid="profile-name" />
                <Field label="Ubicación" defaultValue={profile.ubicacion} onBlur={(e) => saveProfile({ ubicacion: e.target.value })} data-testid="profile-ubicacion" />
                <Field label="Teléfono" defaultValue={profile.telefono} onBlur={(e) => saveProfile({ telefono: e.target.value })} data-testid="profile-telefono" />
                <Field label="Mail de contacto" defaultValue={profile.contacto_email} onBlur={(e) => saveProfile({ contacto_email: e.target.value })} data-testid="profile-contacto-email" />
                <Field label="Dirección" defaultValue={profile.direccion} onBlur={(e) => saveProfile({ direccion: e.target.value })} data-testid="profile-direccion" />
                <Field label="LinkedIn" defaultValue={profile.linkedin} onBlur={(e) => saveProfile({ linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" data-testid="profile-linkedin" />
                <div className="pt-1">
                  <span className="text-xs font-semibold text-[var(--brand-primary)]">Foto de perfil (opcional)</span>
                  <div className="flex items-center gap-3 mt-1">
                    {profile.foto_perfil ? <FotoThumb path={profile.foto_perfil} /> : <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-[var(--brand-border)]" />}
                    <label className="btn-outline inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="foto-label"><Upload size={14} /> Subir foto
                      <input type="file" accept=".png,.jpg,.jpeg,.webp" hidden onChange={uploadFoto} data-testid="foto-input" /></label>
                  </div>
                </div>
                <div className="mt-3 rounded-xl border-2 border-[var(--brand-border)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-[var(--brand-primary)]">Visible para empresas</span>
                    <button type="button" role="switch" aria-checked={!!profile.visible_para_empresas?.activo} onClick={toggleVisibleEmpresas} data-testid="visible-empresas-toggle"
                      className="relative w-12 h-6 rounded-full transition-colors" style={{ background: profile.visible_para_empresas?.activo ? "var(--status-contactado)" : "#cbd5e1" }}>
                      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: profile.visible_para_empresas?.activo ? "1.6rem" : "0.15rem" }} />
                    </button>
                  </div>
                  <p className="text-xs text-[var(--brand-text-muted)] mt-2 leading-relaxed">Tu perfil es visible para las empresas registradas — podés desactivarlo cuando quieras. Aunque lo desactives, tus postulaciones a búsquedas puntuales siguen visibles para esa empresa.</p>
                  {profile.visible_para_empresas?.activo && profile.visible_para_empresas?.fecha_activacion && (
                    <p className="text-[10px] text-[var(--brand-text-muted)] mt-1" data-testid="visible-empresas-fecha">Activado el {new Date(profile.visible_para_empresas.fecha_activacion).toLocaleDateString("es-AR")}</p>
                  )}
                </div>
              </div>
            </Card>
            <Card>
              <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-4">Formación</h3>
              {(profile.formacion || []).map((f, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100">
                  <div><div className="font-semibold text-sm text-[var(--brand-primary)]">{f.programa}</div><div className="text-xs text-[var(--brand-text-muted)]">{f.institucion} · {f.fecha}</div></div>
                  <button onClick={() => saveProfile({ formacion: profile.formacion.filter((_, x) => x !== i) })} className="text-xs text-red-400" data-testid={`form-remove-${i}`}>Quitar</button>
                </div>
              ))}
              <AddFormacion catalogs={catalogs} marcas={["Córdoba Crea Futuro"]} onAdd={(nf) => saveProfile({ formacion: [...(profile.formacion || []), nf] })} />
            </Card>
            <Card>
              <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-4">Certificados</h3>
              {(profile.certificados || []).map((c) => (
                <div key={c.id} className="flex items-center gap-2 py-2 border-b border-slate-100 text-sm">
                  <span className="text-[var(--brand-primary)]">{c.original_filename}</span>
                  {c.verificado && <Chip accent>Verificado</Chip>}
                </div>
              ))}
              <label className="btn-outline inline-flex items-center gap-2 mt-4 cursor-pointer" data-testid="cert-upload-label">
                <Upload size={16} /> Subir certificado
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" hidden onChange={uploadCert} data-testid="cert-upload-input" />
              </label>
            </Card>
            <Card className="lg:col-span-2 border-2 border-red-200" data-testid="danger-zone">
              <h3 className="font-heading font-bold text-red-600 mb-2">Eliminar mi cuenta</h3>
              <p className="text-sm text-[var(--brand-text-muted)] mb-3">Borra tu cuenta y todos tus datos de forma permanente: perfil, postulaciones, emprendimientos y archivos. Esta acción no se puede deshacer (Ley 25.326 de Protección de Datos Personales).</p>
              {!del.open ? (
                <button onClick={() => setDel({ open: true, password: "", confirm: "" })} className="btn-outline text-sm text-red-600 border-red-300" data-testid="delete-account-open"><Trash2 size={14} className="inline mr-1" /> Eliminar mi cuenta</button>
              ) : (
                <div className="space-y-3 max-w-sm">
                  <Field label="Ingresá tu contraseña" type="password" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} data-testid="delete-password" />
                  <Field label='Escribí ELIMINAR para confirmar' value={del.confirm} onChange={(e) => setDel({ ...del, confirm: e.target.value })} data-testid="delete-confirm" />
                  <div className="flex gap-2">
                    <button onClick={deleteAccount} disabled={del.confirm !== "ELIMINAR"} className="btn-primary text-sm disabled:opacity-50" style={{ background: "#dc2626" }} data-testid="delete-account-confirm">Eliminar definitivamente</button>
                    <button onClick={() => setDel({ open: false, password: "", confirm: "" })} className="btn-outline text-sm" data-testid="delete-account-cancel">Cancelar</button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {tab === "habilidades" && (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
          <Card>
            <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-1">Hard skills (técnicas)</h3>
            <p className="text-sm text-[var(--brand-text-muted)] mb-3">Elegí tus habilidades técnicas del catálogo (no texto libre). Al declarar una formación de CCF sumamos sus habilidades; podés quitarlas o agregar otras.</p>
            <select data-testid="hard-select" onChange={(e) => { const v = e.target.value; if (v) { const cur = profile.skills_hard || []; if (!cur.some((s) => s.nombre === v)) saveProfile({ skills_hard: [...cur, { nombre: v }] }); e.target.value = ""; } }}
              className="w-full rounded-lg border-2 border-[var(--brand-border)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-accent)]">
              <option value="">Agregar del catálogo…</option>
              {catalogs.skill_hard.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="flex flex-wrap gap-2 mt-3">
              {(profile.skills_hard || []).map((s) => (
                <span key={s.nombre} className="chip" data-testid={`hard-chip-${s.nombre}`}>{s.nombre}
                  <button className="ml-1" aria-label={`Quitar ${s.nombre}`} data-testid={`hard-chip-remove-${s.nombre}`} onClick={() => saveProfile({ skills_hard: profile.skills_hard.filter((x) => x.nombre !== s.nombre) })}><X size={12} /></button>
                </span>
              ))}
            </div>
          </Card>
          <FortalezasSelector informe={informe} onSaved={loadInforme} />
          </div>
        )}

        {tab === "experiencia" && (
          <PortfolioTab profile={profile} onSave={saveProfile} onUploadCv={uploadCvPropio} onUploadImg={uploadPortfolioImg} />
        )}

        {tab === "ia" && (
          <Card className="max-w-3xl">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="text-[var(--brand-accent)]" size={20} /><h3 className="font-heading font-bold text-[var(--brand-primary)]">Asistente de perfil profesional</h3></div>
            <p className="text-sm text-[var(--brand-text-muted)] mb-4">La IA redacta y ordena únicamente los datos que cargaste (formación, skills, fortalezas visibles). No inventa información. Podés editar el texto antes de guardar.</p>
            <button onClick={generateBio} disabled={genLoading} className="btn-primary flex items-center gap-2 mb-4" data-testid="ai-generate">
              <Sparkles size={16} /> {genLoading ? "Generando…" : "Generar con IA"}
            </button>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="ai-bio-text"
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-3 text-sm min-h-[180px] outline-none focus:border-[var(--brand-accent)]" placeholder="Tu perfil profesional aparecerá acá…" />
            <div className="flex gap-3 mt-4">
              <button onClick={saveCv} className="btn-accent flex items-center gap-2" data-testid="ai-save"><Send size={16} /> Guardar</button>
              <button onClick={downloadPdf} className="btn-outline flex items-center gap-2" data-testid="ai-download-pdf"><Download size={16} /> Descargar CV PDF</button>
            </div>
          </Card>
        )}

        {tab === "empleos" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-6">
              <input placeholder="Buscar puesto…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadBusquedas()} data-testid="empleos-q"
                className="rounded-full border-2 border-[var(--brand-border)] px-4 py-2 text-sm outline-none focus:border-[var(--brand-accent)] flex-1 min-w-[200px]" />
              <input placeholder="Ubicación" value={filters.ubicacion} onChange={(e) => setFilters({ ...filters, ubicacion: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadBusquedas()} data-testid="empleos-ubic"
                className="rounded-full border-2 border-[var(--brand-border)] px-4 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
              <button onClick={loadBusquedas} className="btn-primary" data-testid="empleos-buscar">Buscar</button>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {busquedas.map((b) => (
                <Card key={b.id} data-testid={`empleo-${b.id}`}>
                  <div className="flex justify-between items-start"><h3 className="font-heading font-bold text-[var(--brand-primary)]">{b.titulo}</h3><Chip accent>{b.modalidad}</Chip></div>
                  <p className="text-xs text-[var(--brand-text-muted)] mt-1">{b.empresa_nombre} · {b.ubicacion}</p>
                  <p className="text-sm text-[var(--brand-primary)] mt-3 line-clamp-2">{b.descripcion}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">{(b.skills_requeridas_hard || []).slice(0, 4).map((s) => <Chip key={s}>{s}</Chip>)}</div>
                  <button disabled={b.ya_postulado} onClick={() => postular(b.id)} data-testid={`postular-${b.id}`}
                    className={`mt-4 ${b.ya_postulado ? "btn-outline opacity-60" : "btn-primary"}`}>{b.ya_postulado ? "Ya postulado" : "Postularme"}</button>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === "postulaciones" && (
          <div className="space-y-3">
            {posts.map((p) => (
              <Card key={p.id} className="flex flex-wrap items-center justify-between gap-3" data-testid={`postulacion-${p.id}`}>
                <div><div className="font-heading font-bold text-[var(--brand-primary)]">{p.busqueda_titulo}</div><div className="text-xs text-[var(--brand-text-muted)]">{new Date(p.fecha).toLocaleDateString("es-AR")}</div></div>
                <StatusBadge estado={p.estado} label={p.estado === "descartada" ? "Proceso finalizado" : undefined} testid={`post-estado-${p.id}`} />
                {p.estado === "descartada" && <ProcesoFinalizado id={p.id} onVerOfertas={() => setTab("empleos")} />}
              </Card>
            ))}
            {posts.length === 0 && <p className="text-[var(--brand-text-muted)]">Todavía no te postulaste a ninguna búsqueda.</p>}
          </div>
        )}

        {tab === "emprendimiento" && (
          <Card className="max-w-2xl">
            <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-1">Mi vidriera</h3>
            <p className="text-sm text-[var(--brand-text-muted)] mb-5">Publicá un producto o servicio en el directorio público de emprendedores.</p>
            <div className="space-y-3">
              <Field label="Nombre del producto/servicio" value={emp?.nombre || ""} onChange={(e) => setEmp({ ...emp, nombre: e.target.value })} data-testid="emp-nombre" />
              <Field label="Descripción" textarea value={emp?.descripcion || ""} onChange={(e) => setEmp({ ...emp, descripcion: e.target.value })} data-testid="emp-desc" />
              <Field label="Categoría" value={emp?.categoria || ""} onChange={(e) => setEmp({ ...emp, categoria: e.target.value })} data-testid="emp-cat" />
              <Field label="Contacto (WhatsApp/email)" value={emp?.contacto || ""} onChange={(e) => setEmp({ ...emp, contacto: e.target.value })} data-testid="emp-contacto" />
              <Field label="Redes sociales" value={emp?.redes_sociales || ""} onChange={(e) => setEmp({ ...emp, redes_sociales: e.target.value })} placeholder="Instagram, Facebook, TikTok…" data-testid="emp-redes" />
              <Field label="Trabajos realizados" textarea value={emp?.trabajos || ""} onChange={(e) => setEmp({ ...emp, trabajos: e.target.value })} placeholder="Contá o listá los trabajos que hiciste" data-testid="emp-trabajos" />
              <Field label="Tarifas (opcional)" value={emp?.tarifas || ""} onChange={(e) => setEmp({ ...emp, tarifas: e.target.value })} data-testid="emp-tarifas" />
              <Field label="Comentarios / testimonios (uno por línea)" textarea value={emp?.testimonios || ""} onChange={(e) => setEmp({ ...emp, testimonios: e.target.value })} placeholder="Ej: María G. · Excelente servicio" data-testid="emp-testimonios" />
              <div><span className="text-xs font-semibold text-[var(--brand-primary)]">Skills aplicadas</span>
                <div className="mt-1"><TagInput tags={emp?.skills_aplicadas || []} testid="emp-skills" onChange={(t) => setEmp({ ...emp, skills_aplicadas: t })} /></div>
              </div>
              <button onClick={saveEmp} className="btn-primary" data-testid="emp-save">Guardar en la vidriera</button>
            </div>
          </Card>
        )}

        {tab === "notif" && (
          <div className="space-y-3 max-w-2xl">
            {unread > 0 && <button onClick={() => { api.put("/egresado/notifications/read").then(() => api.get("/egresado/notifications").then((r) => setNotifs(r.data.items))); }} className="btn-outline text-sm" data-testid="notif-read-all">Marcar todo como leído</button>}
            {notifs.map((n) => (
              <Card key={n.id} className={`flex items-start gap-3 ${!n.leida ? "border-l-4 border-l-[var(--brand-accent)]" : ""}`} data-testid={`notif-${n.id}`}>
                <Bell size={18} className="text-[var(--brand-accent)] mt-0.5" />
                <div><p className="text-sm text-[var(--brand-primary)]">{n.texto}</p><p className="text-xs text-[var(--brand-text-muted)] mt-1">{new Date(n.created_at).toLocaleString("es-AR")}</p></div>
              </Card>
            ))}
            {notifs.length === 0 && <p className="text-[var(--brand-text-muted)]">No tenés novedades aún.</p>}
          </div>
        )}
      </Section>
    </div>
  );
}

const PortfolioImg = ({ item, className = "aspect-square" }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { let ok = true; signedFileUrl(item.storage_path).then((u) => ok && setSrc(u)).catch(() => {}); return () => { ok = false; }; }, [item.storage_path]);
  return (
    <div className={`relative ${className} rounded-xl overflow-hidden border-2 border-[var(--brand-border)] bg-slate-50`}>
      {src ? <img src={src} alt={item.titulo || "portfolio"} className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse bg-slate-100" />}
    </div>
  );
};

const FotoThumb = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { let ok = true; signedFileUrl(path).then((u) => ok && setSrc(u)).catch(() => {}); return () => { ok = false; }; }, [path]);
  return <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[var(--brand-border)] bg-slate-50">{src && <img src={src} alt="foto" className="w-full h-full object-cover" />}</div>;
};

const reorder = (arr, from, to) => { const a = [...arr]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; };

const PortfolioTab = ({ profile, onSave, onUploadCv, onUploadImg }) => {
  const [item, setItem] = useState({ tipo: "link", url: "", titulo: "" });
  const [dragImg, setDragImg] = useState(null);
  const [dragLink, setDragLink] = useState(null);
  const items = profile.portfolio || [];
  const imageItems = items.filter((p) => p.storage_path);
  const linkItems = items.filter((p) => !p.storage_path);
  const add = () => { if (!item.url.trim()) return; onSave({ portfolio: [...items, item] }); setItem({ tipo: "link", url: "", titulo: "" }); };
  const remove = (p) => onSave({ portfolio: items.filter((x) => x !== p) });
  const dropImg = (to) => { if (dragImg === null || dragImg === to) return setDragImg(null); onSave({ portfolio: [...reorder(imageItems, dragImg, to), ...linkItems] }); setDragImg(null); };
  const dropLink = (to) => { if (dragLink === null || dragLink === to) return setDragLink(null); onSave({ portfolio: [...imageItems, ...reorder(linkItems, dragLink, to)] }); setDragLink(null); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <h3 className="font-heading font-bold text-[var(--brand-primary)]">Mi experiencia</h3>
        <p className="text-sm text-[var(--brand-text-muted)] mt-1 mb-4">Subí imágenes (PNG, JPG, WEBP · máx 5MB), o agregá videos (YouTube/Vimeo/Loom) y links. Arrastrá para reordenar y destacar tu mejor trabajo primero.</p>

        <label className="btn-accent inline-flex items-center gap-2 cursor-pointer text-sm mb-4" data-testid="portfolio-img-label">
          <Upload size={15} /> Subir imagen
          <input type="file" accept=".png,.jpg,.jpeg,.webp" hidden onChange={onUploadImg} data-testid="portfolio-img-input" />
        </label>

        {imageItems.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-5" data-testid="portfolio-images">
            {imageItems.map((p, idx) => (
              <div key={idx} className="relative group" draggable data-testid={`portfolio-image-${idx}`}
                onDragStart={() => setDragImg(idx)} onDragEnd={() => setDragImg(null)}
                onDragOver={(e) => e.preventDefault()} onDrop={() => dropImg(idx)}
                style={{ opacity: dragImg === idx ? 0.4 : 1, cursor: "grab" }}>
                <PortfolioImg item={p} />
                <button onClick={() => remove(p)} data-testid={`portfolio-image-remove-${idx}`}
                  className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}

        {linkItems.map((p, idx) => (
          <div key={idx} className="flex items-center justify-between gap-2 py-2 border-b border-slate-100" draggable
            data-testid={`portfolio-item-${idx}`}
            onDragStart={() => setDragLink(idx)} onDragEnd={() => setDragLink(null)}
            onDragOver={(e) => e.preventDefault()} onDrop={() => dropLink(idx)}
            style={{ opacity: dragLink === idx ? 0.4 : 1, cursor: "grab" }}>
            <a href={p.url} target="_blank" rel="noreferrer" className="text-sm text-[var(--brand-primary)] flex items-center gap-2 truncate">
              {p.tipo === "link" ? <Link2 size={14} /> : <ExternalLink size={14} />}{p.titulo || p.url}<span className="chip">{p.tipo}</span>
            </a>
            <button onClick={() => remove(p)} className="text-red-400" data-testid={`portfolio-remove-${idx}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="grid grid-cols-4 gap-2 mt-4">
          <select value={item.tipo} onChange={(e) => setItem({ ...item, tipo: e.target.value })} data-testid="portfolio-tipo" className="rounded-lg border-2 border-[var(--brand-border)] px-2 py-2 text-sm">
            {["link", "video", "documento"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input placeholder="URL" value={item.url} onChange={(e) => setItem({ ...item, url: e.target.value })} data-testid="portfolio-url" className="col-span-2 rounded-lg border-2 border-[var(--brand-border)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
          <input placeholder="Título" value={item.titulo} onChange={(e) => setItem({ ...item, titulo: e.target.value })} data-testid="portfolio-titulo" className="rounded-lg border-2 border-[var(--brand-border)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
        </div>
        <button onClick={add} className="btn-outline text-sm mt-3 flex items-center gap-1.5" data-testid="portfolio-add"><Plus size={15} /> Agregar enlace</button>
      </Card>
      <Card>
        <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-3">CV propio</h3>
        <p className="text-xs text-[var(--brand-text-muted)] mt-1 mb-1">CV propio (PDF, distinto del generado por IA). Tu LinkedIn ahora se carga en "Mi Perfil".</p>
        {profile.cv_propio && <p className="text-sm text-[var(--brand-primary)] mb-2">{profile.cv_propio.original_filename}</p>}
        <label className="btn-outline inline-flex items-center gap-2 cursor-pointer" data-testid="cv-propio-label">
          <Upload size={16} /> Subir CV propio
          <input type="file" accept=".pdf" hidden onChange={onUploadCv} data-testid="cv-propio-input" />
        </label>
      </Card>
    </div>
  );
};

const AddFormacion = ({ catalogs, marcas, onAdd }) => {
  const [f, setF] = useState({ programa: "", institucion: marcas[0], fecha: "" });
  return (
    <div className="mt-4 grid grid-cols-2 gap-2">
      <select value={f.programa} onChange={(e) => setF({ ...f, programa: e.target.value })} data-testid="form-programa"
        className="rounded-lg border-2 border-[var(--brand-border)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-accent)]">
        <option value="">Elegí una formación…</option>
        {catalogs.formacion.map((p) => <option key={p} value={p}>{p}</option>)}
        <option value="Otra">Otra (texto libre)</option>
      </select>
      <select value={f.institucion} onChange={(e) => setF({ ...f, institucion: e.target.value })} data-testid="form-institucion" className="rounded-lg border-2 border-[var(--brand-border)] px-3 py-2 text-sm">
        {marcas.map((m) => <option key={m}>{m}</option>)}
      </select>
      <input placeholder="Año" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} data-testid="form-fecha" className="rounded-lg border-2 border-[var(--brand-border)] px-3 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
      <button onClick={() => { if (f.programa) { onAdd({ ...f, marca: f.institucion }); setF({ programa: "", institucion: marcas[0], fecha: "" }); } }} className="btn-accent text-sm" data-testid="form-add">Agregar</button>
    </div>
  );
};
